import { zodResolver } from "@hookform/resolvers/zod";
import { Delete } from "@mui/icons-material";
import { Alert, Button, IconButton, MenuItem, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactNode, useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import {
  CreateContactFieldUISchema,
  createContactFieldUISchemaWithDuplicateValidation,
} from "@stw/audience-contact-fields";

import {
  AudienceContactFieldType,
  CreateAudienceContactFieldMutation,
  CreateAudienceContactFieldMutationVariables,
  UpdateAudienceContactFieldMutation,
  UpdateAudienceContactFieldMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import {
  CREATE_CONTACT_FIELD,
  GET_AUDIENCE_CONTACT_FIELDS,
  UPDATE_CONTACT_FIELD,
} from "~frontend/graphql/audience";
import useMutation from "~frontend/util/useMutation";

import { FieldTypesByDbType } from "./ViewFields";

type FormData = z.infer<typeof CreateContactFieldUISchema>;

export const AddOrEditField = ({
  audienceId,
  onClose,
  field,
  existingFields = [],
}: {
  audienceId: string;
  onClose: () => void;
  field:
    | {
        id: string;
        name: string;
        type: AudienceContactFieldType;
        typeParameters: {
          values?: string[] | null;
          maxLength?: number | null;
        };
      }
    | "NEW";
  existingFields?: Array<{
    id: string;
    name: string;
    type: AudienceContactFieldType;
  }>;
}): ReactNode => {
  const { enqueueSnackbar } = useSnackbar();
  const isNew = field === "NEW";
  const copy = `
    ${isNew ? "Create a new" : "Edit your"} custom field for your short code 
    subscriber audience to use in segmenting your contacts or customizing your 
    messaging. Once created, you can map a field from a CSV upload or your 
    integration opt ins to this custom field.
  `;

  const defaultValues: FormData = useMemo(() => {
    return isNew
      ? {
          name: "",
          type: "STRING" as const,
          typeParameters: { maxLength: 255 },
        }
      : CreateContactFieldUISchema.parse({
          name: field.name,
          type: field.type,
          typeParameters:
            field.type === "ENUM"
              ? {
                  // useFieldArray doesn't work with string[], must be object[]
                  values:
                    field.typeParameters?.values?.map((value: string) => ({
                      value: `${value}`,
                    })) || [],
                }
              : field.typeParameters || {},
        });
  }, [isNew, field]);

  const [createContactField] = useMutation<
    CreateAudienceContactFieldMutation,
    CreateAudienceContactFieldMutationVariables
  >(CREATE_CONTACT_FIELD, {
    refetchQueries: [GET_AUDIENCE_CONTACT_FIELDS],
  });

  const [updateContactField] = useMutation<
    UpdateAudienceContactFieldMutation,
    UpdateAudienceContactFieldMutationVariables
  >(UPDATE_CONTACT_FIELD, {
    refetchQueries: [GET_AUDIENCE_CONTACT_FIELDS],
  });

  const handleCreateField = async (data: FormData) => {
    if (field && !isNew) {
      throw new Error("Cannot create a new field when editing an existing one");
    }

    const { name, type, typeParameters } =
      CreateContactFieldUISchema.parse(data);

    const { errors } = await createContactField({
      variables: {
        input: {
          audienceId,
          name,
          type: type as AudienceContactFieldType,
          typeParameters:
            type === "ENUM"
              ? {
                  values: (
                    typeParameters as { values: { value: string }[] }
                  ).values.map(val => val.value),
                }
              : typeParameters,
        },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to create field", { variant: "error" });
    } else {
      // TODO: Show success message in modal instead of snackbar
      enqueueSnackbar("Field created successfully", { variant: "success" });
      onClose();
    }
  };

  const handleUpdateField = async (data: FormData) => {
    if (!field || isNew) {
      throw new Error("Cannot update a new field");
    }

    const { errors } = await updateContactField({
      variables: {
        input: {
          id: field.id,
          audienceId,
          name: data.name,
          typeParameters:
            data.type === "ENUM"
              ? {
                  values: (
                    data.typeParameters as { values: { value: string }[] }
                  ).values.map(val => val.value),
                }
              : data.typeParameters,
        },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to update field", { variant: "error" });
    } else {
      enqueueSnackbar("Field updated successfully", { variant: "success" });
      onClose();
    }
  };

  const handleCreateOrEditField = async (data: FormData) => {
    if (isNew) {
      await handleCreateField(data);
    } else {
      await handleUpdateField(data);
    }
  };

  const validationSchema = useMemo(() => {
    const existingNames = existingFields
      .filter(f => isNew || f.id !== field?.id)
      .map(f => f.name);

    return createContactFieldUISchemaWithDuplicateValidation(existingNames);
  }, [existingFields, isNew, field]);

  const { control, watch, setValue, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(validationSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    values: defaultValues,
    shouldUnregister: false,
  });

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "typeParameters.values",
  });

  const fieldType = watch("type") as AudienceContactFieldType;
  useEffect(() => {
    if (!isNew) {
      return;
    }

    const params =
      (
        FieldTypesByDbType[fieldType] as
          | { typeParameters: any | undefined }
          | undefined
      )?.typeParameters || {};

    setValue("typeParameters", params);
  }, [fieldType, isNew, setValue]);

  return (
    <form onSubmit={handleSubmit(handleCreateOrEditField)}>
      <FlexBox direction="column" gap={3}>
        <Typography variant="h1">
          {isNew ? "Create" : "Edit"} Custom Field
        </Typography>
        <Typography>{copy}</Typography>
        <FlexBox direction="column" gap={1}>
          <RhfTextField
            control={control}
            name="name"
            label="Custom Field Name"
            characterLimit={50}
          />
          <RhfSelect
            control={control}
            name="type"
            label="Field Type"
            disabled={!isNew}
          >
            {Object.values(FieldTypesByDbType).map(fieldType => (
              <MenuItem key={fieldType.type} value={fieldType.type}>
                {fieldType.label}
              </MenuItem>
            ))}
          </RhfSelect>
          {fieldType === "STRING" && (
            <RhfTextField
              control={control}
              name="typeParameters.maxLength"
              label="Max Length"
              InputProps={{ inputProps: { min: 1 } }}
            />
          )}
          {fieldType === "ENUM" && (
            <FlexBox direction="column" gap={0.5} sx={{ pl: 3 }}>
              {fields.map((f, index) => (
                <FlexBox key={f.id} alignItems="center">
                  <RhfTextField
                    control={control}
                    name={`typeParameters.values.${index}.value`}
                    label={`Single Select Option ${index + 1}`}
                    style={{ flexGrow: 1 }}
                    characterLimit={50}
                  />
                  <IconButton onClick={() => remove(index)}>
                    <Delete />
                  </IconButton>
                </FlexBox>
              ))}
            </FlexBox>
          )}
        </FlexBox>
        {!isNew && (
          <Alert severity="warning">
            <strong>Warning:</strong> A custom field's field type can not be
            changed once created.{" "}
            {fieldType === "ENUM"
              ? "Deleting a single select option will remove it from all contacts it was assigned to and leave this custom field blank in their record."
              : ""}
          </Alert>
        )}
        <FlexBox justifyContent="space-between" gap={1}>
          {fieldType === "ENUM" ? (
            <Button
              variant="contained"
              color="tertiary"
              onClick={() => {
                append({ value: "" });
              }}
            >
              + Add a Single Select Option
            </Button>
          ) : (
            <div />
          )}
          <FlexBox justifyContent="flex-end" gap={1}>
            <Button variant="contained" color="tertiary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" type="submit">
              {isNew ? "Create Field" : "Save Changes"}
            </Button>
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </form>
  );
};
